import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import numeral from 'numeral'
import IntegrationAuthProvider from '../../../IntegrationAuthProvider/IntegrationAuthProvider'
import Icon from '../../../../atoms/Icon'
import Loading from '../../../../molecules/Loading'
import FlexRow from '../../../../molecules/FlexRow'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import useRedtailMetricPreset from './useRedtailMetricPreset'

const SIZE_STYLES = {
  sm: { titleFontSize: '0.875rem', metricFontSize: '1.5rem', iconSize: '32px', padding: '8px', minHeight: '72px' },
  md: { titleFontSize: '1rem', metricFontSize: '2rem', iconSize: '40px', padding: '10px', minHeight: '92px' },
  lg: { titleFontSize: '1.25rem', metricFontSize: '2.5rem', iconSize: '50px', padding: '12px', minHeight: '116px' }
}

function RedtailMetricInner ({ config, styleVariant }) {
  const presetKey = config?.preset
  const { value, items, DetailPanel, isLoading, notLinked, title, label, icon, format } = useRedtailMetricPreset(presetKey, config)
  const [panelOpen, setPanelOpen] = useState(false)

  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md
  const interactive = !!DetailPanel

  const handleOpen = useCallback(() => {
    if (interactive) setPanelOpen(true)
  }, [interactive])

  const handleClose = useCallback(() => {
    setPanelOpen(false)
  }, [])

  const handleKeyDown = useCallback((e) => {
    if (interactive && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      setPanelOpen(true)
    }
  }, [interactive])

  if (!presetKey) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        No preset configured for this metric component.
      </div>
    )
  }

  if (isLoading) {
    return <Loading />
  }

  if (notLinked) {
    return (
      <FadeIn>
        <FlexRow className='justify-center items-center' style={{ minHeight: sizes.minHeight, color: 'var(--text-color-subtle)', fontSize: 'var(--font-size-xs)' }}>
          Client is not linked to Redtail
        </FlexRow>
      </FadeIn>
    )
  }

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', padding: sizes.padding }}>
      {icon && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px', fill: 'var(--color-primary-500)' }}>
          <Icon name={icon} customSize={sizes.iconSize} />
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ fontSize: sizes.titleFontSize }}>
            {title}
          </div>
        )}
        <div style={{ fontSize: sizes.metricFontSize }}>
          {value != null ? numeral(value).format(format) : '--'}
        </div>
        {label && (
          <div style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
            {label}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <FadeIn>
      {interactive
        ? (
          <div
            role='button'
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            style={{ cursor: 'pointer' }}
          >
            {cardContent}
          </div>
        )
        : cardContent}
      {interactive && (
        <DetailPanel
          open={panelOpen}
          onClose={handleClose}
          items={items}
          title={title}
        />
      )}
    </FadeIn>
  )
}

RedtailMetricInner.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string
}

function RedtailMetricRenderer ({ config, styleVariant, presentationKey, blueprint, displayName }) {
  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md

  return (
    <div style={{ minHeight: sizes.minHeight }}>
      <IntegrationAuthProvider integration='redtail' size='sm' loadingType='circular'>
        <RedtailMetricInner
          config={config}
          styleVariant={styleVariant}
          presentationKey={presentationKey}
          blueprint={blueprint}
          displayName={displayName}
        />
      </IntegrationAuthProvider>
    </div>
  )
}

RedtailMetricRenderer.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string,
  presentationKey: PropTypes.string,
  blueprint: PropTypes.object,
  displayName: PropTypes.string
}

export default RedtailMetricRenderer
